import { type yup } from '@framework/validation/initYup.ts';
import { setMetaWhen } from '@framework/validation/methods/metaWhen.ts';
import { WidgetDirectivesRepository } from '../../schema/directives/WidgetDirectivesRepository.ts';
import { type IRule } from '../../schema/rules/IRule.ts';
import { extractRule } from '../../schema/rules/repository/extractRule.ts';
import {
  type IParentWidgetScope,
  type IWidgetType,
} from '../../schema/widgets/IWidgetType.ts';
import { setConfirmationFormSchemaRules } from '../../schema/widgets/repository/advanced/confirm/shared/setConfirmationFormSchemaRules.ts';
import { isInputWidget } from '../../schema/widgets/repository/inputs/isInputWidget.ts';
import { type Widget } from '../../schema/widgets/Widget.ts';
import { setEnabledCondition } from '../setEnabledCondition.ts';
import { setVisibleCondition } from '../setVisibleCondition.ts';
import { setWidgetDefaultValue } from '../utils/setWidgetDefaultValue.ts';
import { setWidgetSchemaLabel } from '../utils/setWidgetSchemaLabel.ts';
import { FormBuilderMetaFlags } from './flags/FormBuilderMetaFlags.ts';

/**
 * Imposta tutta la validazione seguendo la configurazione delle regole di validazione,
 * di visibilità, le label etc.
 */
export function setSchemaRules<W extends Widget>(
  inputSchema: yup.AnySchema,
  options: {
    widget: W;
    widgetType: IWidgetType<W> | null;
    parentScope: IParentWidgetScope;
  },
) {
  const { widget, widgetType, parentScope } = options;

  // NB non possiamo fare check su schema.spec.optional per gestire il required
  // perché qui non è ancora calcolato.
  // schema = schema.notRequired();

  if (!widgetType) return inputSchema;

  const flags = inputSchema.meta?.() ?? {};

  // setEditableRule deve essere chiamato prima di setValidationRules
  // per poter leggere il valore meta 'enabled' dello schema
  let schema: yup.AnySchema = setEditableRule(
    inputSchema,
    widget,
    widgetType,
    parentScope,
  );

  // Le regole di validazione (required, range, format, ...) vengono
  // applicate _solo_ se il meta "applyRules" è true (o undefined).
  if (!flags[FormBuilderMetaFlags.SkipValidationRules]) {
    schema = setValidationRules(schema, widget, widgetType);
  }

  // Se configurato, applica le regole di validazione post-rules
  schema = setPostApplyRulesWhen(schema);

  // Aggiungiamo tutte le regole di validazione definite nelle direttive
  const directives = WidgetDirectivesRepository.getConfiguredInWidget(widget);

  for (const directive of directives) {
    if (!directive.directive.formSchemaModifier) continue;

    schema = directive.directive.formSchemaModifier({
      ...options,
      widgetType,
      schema,
      flags,
      directiveConfig: directive.directiveConfig,
    });
  }

  schema = setVisibilityRule(schema, widget, widgetType, parentScope);

  if (!flags[FormBuilderMetaFlags.SkipDefaultValue]) {
    schema = defaultValue(schema, widget, widgetType);
  }

  schema = setConfirmationFormSchemaRules(schema, flags, options);
  if (!flags[FormBuilderMetaFlags.SkipLabel]) {
    schema = setLabel(schema, widget, widgetType);
  }

  return schema;
}

/**
 * Imposta il validatore in base alle regole di validazione configurate.
 * Le regole vengono applicate solo se il flag nei meta "applyRules" è true (o undefined).
 * questo permette di gestire la validazione parziale.
 * Non è possibile mettere la setValidationRules direttamente nel when della
 * validazione parziale, perché yup non ordina i campi in base alle dipendenze
 * dei when annidati (le dipendenze sono calcolate a build-time e non a run-time).
 */
export function setValidationRules<W extends Widget>(
  schema: yup.AnySchema,
  widget: W,
  widgetType: IWidgetType<W>,
) {
  if (!isInputWidget(widget)) return schema;

  // 1. Regole dinamiche, così come configurate concretamente nel widget
  const dynamicRules = (widgetType.rules ?? [])
    .map(rule => ({
      rule,
      config: extractRule<IRule>(widget.rules, rule.type),
    }))
    .filter(
      (entry): entry is { rule: (typeof entry)['rule']; config: IRule } =>
        entry.config != null,
    );

  // 2. Regole statiche, sono inserite nel `widgetType` solo
  //    per evitare che siano applicate in fase di compilazione
  //    se si è in una validazione parziale, in un'altra sezione, ecc.
  const fixedRules = widgetType.fixedRules ?? [];

  if (dynamicRules.length === 0 && fixedRules.length === 0) return schema;

  // Consolidare tutte le regole in un singolo .when() per ridurre
  // il numero di Condition objects e cloni a runtime
  return schema.when([], (_, schema) => {
    const applyRules = schema.meta?.()?.applyRules ?? true;
    // Controllare se il widget non è enabled non applicare le regole di validazione
    const isEnabled = schema.meta?.()?.enabled ?? true;
    if (!applyRules || !isEnabled) return schema;

    return schema.withMutation((s: typeof schema) => {
      for (const { rule, config } of dynamicRules) {
        rule.validator(s, config);
      }
      for (const fixedRule of fixedRules) {
        fixedRule.validator(s, widget);
      }
      return s;
    });
  });
}

/**
 * Imposta la visibilità del widget.
 */
export function setVisibilityRule<W extends Widget>(
  schema: yup.AnySchema,
  widget: W,
  widgetType: IWidgetType<W>,
  parentScope: IParentWidgetScope,
) {
  return setVisibleCondition(schema, widget, widgetType, parentScope);
}

/**
 * Imposta l'attivazione/disattivazione del widget.
 */
export function setEditableRule<W extends Widget>(
  schema: yup.AnySchema,
  widget: W,
  widgetType: IWidgetType<W>,
  parentScope: IParentWidgetScope,
) {
  if (widgetType.alwaysEnabled) return schema.enabled(true);
  const flags = schema.meta?.() ?? {};
  if (flags[FormBuilderMetaFlags.SkipEnabledCondition]) return schema;
  return setEnabledCondition(schema, widget, widgetType, parentScope);
}

/**
 * Imposta la label del widget.
 */
export function setLabel<W extends Widget>(
  schema: yup.AnySchema,
  widget: W,
  widgetType: IWidgetType<W>,
) {
  return setWidgetSchemaLabel(widget, schema);
}

export function defaultValue<W extends Widget>(
  schema: yup.AnySchema,
  widget: W,
  widgetType: IWidgetType<W>,
) {
  return setWidgetDefaultValue(widget, schema);
}

/**
 * Tag dei meta-when utili per impostare i when in varie fasi della costruizione dello schema.
 * In questo modo è possibile definire un when che verrà applicato _dopo_ altre operazioni (e.g. set di meta/regole)
 */
export enum SchemaBuilderWhenTags {
  PostApplyRules = 'postApplyRules',
}

/**
 * Se sono stati configurati dei metaWhen, li applica.
 * In questo modo, è possibile configurare un when nel validatore
 * che verrà però applicato _dopo_ i meta `enable` e `applyRules`.
 *
 * Per esempio, per i campi dei PaymentRow, è possibile configurare il required
 * opzionale soltanto se `applyRules` è true, ma `applyRules` è impostato
 * _dopo_ la creazione dello schema stesso, quindi deve essere configurato
 * come extra when.
 */
export function setPostApplyRulesWhen(schema: yup.AnySchema) {
  return setMetaWhen(SchemaBuilderWhenTags.PostApplyRules, schema);
}
